import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Bitcoin, Check, Copy, Star, Zap, ShieldCheck, Crown } from "lucide-react";

// Color palette per tier — inspired by the reference
const TIER_STYLE = {
  free: {
    accent: "#22C55E",
    subtitleColor: "text-[#22C55E]",
    borderHover: "hover:border-[#22C55E]/60",
    ring: "border-[#22C55E]/25",
    ribbon: null,
    label: "AKTİF PLAN VARSAYILAN",
  },
  bronze: {
    accent: "#3B82F6",
    subtitleColor: "text-[#3B82F6]",
    borderHover: "hover:border-[#3B82F6]/60",
    ring: "border-[#3B82F6]/25",
    ribbon: null,
    label: "BAŞLANGIÇ",
  },
  silver: {
    accent: "#A855F7",
    subtitleColor: "text-[#A855F7]",
    borderHover: "hover:border-[#A855F7]/60",
    ring: "border-[#A855F7]",
    ribbon: "EN POPÜLER",
    label: "PROFESYONEL",
  },
  gold: {
    accent: "#EAB308",
    subtitleColor: "text-[#EAB308]",
    borderHover: "hover:border-[#EAB308]/60",
    ring: "border-[#EAB308]/25",
    ribbon: null,
    label: "ARAŞTIRMACI",
  },
};

const PERIODS = [
  { key: "daily", label: "Günlük", multiplier: 1 / 30 },
  { key: "weekly", label: "Haftalık", multiplier: 1 / 4 },
  { key: "monthly", label: "Aylık", multiplier: 1 },
  { key: "yearly", label: "Yıllık", multiplier: 10, save: "%16" }, // 20 -> 16% saving
  { key: "lifetime", label: "Ömürlük", multiplier: 40 },
];

function formatBtc(v) {
  if (!v) return "0";
  return parseFloat(v.toFixed(6)).toString();
}

function TierCard({ tier, style, period, currentTierKey, onBuy }) {
  const price = tier.price_btc * period.multiplier;
  const isCurrent = currentTierKey === tier.key;
  const isPopular = style.ribbon != null;
  return (
    <div
      className={`relative gc-card p-5 flex flex-col border-2 ${
        isPopular ? style.ring : "border-[#1a1a1a]"
      } transition-colors ${style.borderHover}`}
      data-testid={`tier-card-${tier.key}`}
    >
      {isPopular && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-[10px] font-mono tracking-widest font-bold rounded-full"
          style={{
            background: style.accent,
            color: "#0a0a0a",
          }}
        >
          {style.ribbon}
        </div>
      )}
      <div className="flex items-center justify-between">
        <span
          className="text-[10px] font-mono tracking-widest font-bold"
          style={{ color: style.accent }}
        >
          {style.label}
        </span>
        {tier.key === "gold" ? (
          <Crown size={14} style={{ color: style.accent }} />
        ) : tier.key === "silver" ? (
          <ShieldCheck size={14} style={{ color: style.accent }} />
        ) : tier.key === "bronze" ? (
          <Zap size={14} style={{ color: style.accent }} />
        ) : (
          <Star size={14} style={{ color: style.accent }} />
        )}
      </div>

      <div className="font-heading text-xl font-black mt-2 text-white">
        {tier.name}
      </div>
      <p className={`text-xs ${style.subtitleColor} mt-0.5`}>
        {tier.key === "free"
          ? "Sınırlı ücretsiz erişim"
          : tier.key === "bronze"
          ? "Bireysel hobiciler için"
          : tier.key === "silver"
          ? "Aktif profesyoneller için"
          : "Ajanslar & takımlar için"}
      </p>

      <div className="mt-4">
        <div className="flex items-baseline gap-1.5">
          <span
            className="font-heading text-4xl font-black leading-none"
            style={{ color: tier.price_btc === 0 ? "#EDEDED" : style.accent }}
          >
            {tier.price_btc === 0 ? "₿0" : `₿${formatBtc(price)}`}
          </span>
          <span className="text-xs font-mono text-[#A1A1AA]">
            / {period.label.toLowerCase()}
          </span>
        </div>
        {period.save && tier.price_btc > 0 && (
          <div className="text-[10px] font-mono text-[#22C55E] mt-1">
            {period.save} tasarruf
          </div>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
        <Stat label="Günlük Sorgu" value={tier.daily_limit === -1 ? "∞" : String(tier.daily_limit)} />
        <Stat label="Süre" value={`${tier.duration_days}g`} />
        <Stat label="Platform" value="8" />
        <Stat label="Destek" value={tier.key === "gold" ? "7/24" : tier.key === "silver" ? "Öncelik" : "E-posta"} />
      </div>

      <ul className="mt-4 space-y-1.5 text-xs text-[#EDEDED] flex-1">
        {(tier.features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={12} className="mt-0.5 shrink-0" style={{ color: style.accent }} />
            {f}
          </li>
        ))}
      </ul>

      <button
        disabled={tier.price_btc === 0 || isCurrent}
        onClick={() => onBuy(tier)}
        className={`mt-5 w-full py-2.5 rounded-lg text-xs font-mono uppercase tracking-widest font-bold transition-colors ${
          tier.price_btc === 0 || isCurrent
            ? "border border-[#1a1a1a] text-[#525252] cursor-not-allowed"
            : "text-[#0a0a0a] hover:brightness-110"
        }`}
        style={{
          background: tier.price_btc === 0 || isCurrent ? "transparent" : style.accent,
        }}
        data-testid={`buy-btn-${tier.key}`}
      >
        {isCurrent ? "Aktif Planınız" : tier.price_btc === 0 ? "Varsayılan" : "Satın Al"}
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#141414] bg-[#050505] rounded-lg p-2">
      <div className="text-[9px] font-mono tracking-widest text-[#525252] uppercase">
        {label}
      </div>
      <div className="text-sm font-mono text-white mt-0.5">{value}</div>
    </div>
  );
}

function ComparisonTable({ tiers }) {
  const rows = [
    ["Günlük Sorgu", (t) => (t.daily_limit === -1 ? "Sınırsız" : String(t.daily_limit))],
    ["Süre", (t) => `${t.duration_days} gün`],
    ["Fiyat (aylık)", (t) => (t.price_btc === 0 ? "Ücretsiz" : `₿${t.price_btc}`)],
    ["Discord ID Sorgu", () => "✓"],
    ["Instagram Sorgu", () => "✓"],
    ["Snapchat Sorgu", () => "✓"],
    ["TikTok Sorgu", () => "✓"],
    ["Facebook Sorgu", () => "✓"],
    ["GitHub Sorgu", () => "✓"],
    ["Telegram Sorgu", () => "✓"],
    ["Twitter/X Sorgu", () => "✓"],
    ["Detaylı JSON Sonuç", (t) => (t.key === "free" ? "—" : "✓")],
    ["CSV Export", (t) => (t.key === "silver" || t.key === "gold" ? "✓" : "—")],
    ["API Erişimi", (t) => (t.key === "gold" ? "✓" : "—")],
    ["Öncelikli Destek", (t) => (t.key === "silver" || t.key === "gold" ? "✓" : "—")],
    ["7/24 Canlı Destek", (t) => (t.key === "gold" ? "✓" : "—")],
    ["Yeni özelliklere erken erişim", (t) => (t.key === "gold" ? "✓" : "—")],
  ];
  return (
    <div className="gc-card overflow-hidden">
      <div className="p-5 border-b border-[#141414]">
        <div className="gc-label mb-1">KARŞILAŞTIRMA</div>
        <div className="font-heading text-xl font-bold">Plan Detayları</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left bg-[#050505] border-b border-[#141414]">
              <th className="p-3 font-mono text-[10px] tracking-widest text-[#A1A1AA] uppercase">
                Özellik
              </th>
              {tiers.map((t) => {
                const s = TIER_STYLE[t.key] || TIER_STYLE.free;
                return (
                  <th
                    key={t.key}
                    className="p-3 font-heading text-white text-center"
                    style={{ color: s.accent }}
                  >
                    {t.name}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, fn], i) => (
              <tr
                key={i}
                className={`border-b border-[#141414] ${i % 2 ? "" : "bg-[#080808]"}`}
              >
                <td className="p-3 text-[#EDEDED]">{label}</td>
                {tiers.map((t) => (
                  <td
                    key={t.key}
                    className="p-3 text-center text-[#EDEDED] font-mono text-xs"
                  >
                    {fn(t)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Shop() {
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tx, setTx] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [periodKey, setPeriodKey] = useState("monthly");

  const load = () => {
    api.get("/tiers").then((r) => setTiers(r.data.items || []));
    api.get("/shop/settings").then((r) => setSettings(r.data));
    api.get("/shop/my-orders").then((r) => setOrders(r.data.items || []));
  };
  useEffect(() => {
    load();
  }, []);

  const period = PERIODS.find((p) => p.key === periodKey) || PERIODS[2];
  const orderedTiers = useMemo(() => {
    const order = ["gold", "silver", "free", "bronze"]; // reference layout: Researcher, Pro, Freemium, Beginner
    return [...tiers].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }, [tiers]);

  const submitOrder = async (e) => {
    e.preventDefault();
    setErr("");
    setOk("");
    setBusy(true);
    try {
      await api.post("/shop/buy", { tier_key: selected.key, tx_id: tx });
      setOk("Sipariş oluşturuldu. Admin onayı bekleniyor.");
      setSelected(null);
      setTx("");
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const currentTier = user?.membership_tier || "free";

  return (
    <div className="max-w-none w-full space-y-4">
      {/* HERO */}
      <section
        className="relative overflow-hidden rounded-2xl p-6 md:p-8 border border-[#9D4EDD]/40"
        style={{
          background:
            "radial-gradient(ellipse 900px 400px at 50% 0%, rgba(157,78,221,0.18), transparent 65%), linear-gradient(180deg, rgba(14,4,4,0.85), rgba(4,2,2,0.85))",
        }}
        data-testid="shop-hero"
      >
        <div className="inline-flex items-center gap-1.5 text-[10px] font-mono tracking-widest text-[#9D4EDD] border border-[#9D4EDD]/50 px-2.5 py-1 bg-[#9D4EDD]/10 rounded-full">
          <Crown size={11} /> ÜYELİKLERİ KEŞFET
        </div>
        <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mt-4 leading-[0.95]">
          Erişimini <span className="text-[#9D4EDD]">Yükselt</span>
        </h1>
        <p className="mt-3 max-w-2xl text-sm md:text-base text-[#A1A1AA]">
          Daha çok sorgu, daha fazla platform, öncelikli destek — hepsi Bitcoin
          ile satın alınır. Bir plan seç, TXID'ni gir, üyeliğin anında aktif olsun.
        </p>

        <div className="mt-6 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] font-mono tracking-widest text-[#525252]">
              MEVCUT PLAN
            </div>
            <div className="font-heading text-xl font-bold text-white mt-1">
              {currentTier.toUpperCase()}
            </div>
          </div>
          <div className="h-8 w-px bg-[#1a1a1a] hidden md:block" />
          <div>
            <div className="text-[10px] font-mono tracking-widest text-[#525252]">
              KOTA
            </div>
            <div className="font-heading text-xl font-bold text-white mt-1">
              {user?.queries_today ?? 0}/
              {(tiers.find((t) => t.key === currentTier)?.daily_limit ?? 5) === -1
                ? "∞"
                : tiers.find((t) => t.key === currentTier)?.daily_limit ?? 5}
            </div>
          </div>
          <div className="h-8 w-px bg-[#1a1a1a] hidden md:block" />
          <div>
            <div className="text-[10px] font-mono tracking-widest text-[#525252]">
              ÖDEME YÖNTEMİ
            </div>
            <div className="font-heading text-xl font-bold text-[#9D4EDD] mt-1 flex items-center gap-2">
              <Bitcoin size={16} /> BTC
            </div>
          </div>
        </div>
      </section>

      {/* Billing period tabs */}
      <div className="flex items-center flex-wrap gap-2" data-testid="period-tabs">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriodKey(p.key)}
            data-testid={`period-${p.key}`}
            className={`relative px-4 py-2 text-xs font-mono uppercase tracking-widest rounded-lg border transition-colors ${
              periodKey === p.key
                ? "border-[#9D4EDD] bg-[#9D4EDD]/10 text-white"
                : "border-[#1a1a1a] bg-[#080808] text-[#A1A1AA] hover:border-[#333]"
            }`}
          >
            {p.label}
            {p.save && (
              <span className="absolute -top-2 -right-2 bg-[#22C55E] text-[#0a0a0a] text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                TASARRUF {p.save}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tier grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-3">
        {orderedTiers.map((t) => (
          <TierCard
            key={t.key}
            tier={t}
            style={TIER_STYLE[t.key] || TIER_STYLE.free}
            period={period}
            currentTierKey={currentTier}
            onBuy={(x) => setSelected(x)}
          />
        ))}
      </div>

      {/* Comparison Table */}
      <ComparisonTable tiers={orderedTiers} />

      {/* Buy modal */}
      {selected && settings && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          data-testid="buy-modal"
        >
          <form onSubmit={submitOrder} className="w-full max-w-lg gc-card p-6 relative">
            <div className="gc-label mb-2">BTC ÖDEME</div>
            <h2 className="font-heading text-2xl font-bold mb-1">
              {selected.name} — ₿{selected.price_btc}
            </h2>
            <p className="text-sm text-[#A1A1AA] mb-5">
              Aşağıdaki BTC adresine tam tutarı gönderin, ardından işlem ID&apos;sini
              (TXID) girin. Admin onayı sonrası üyeliğiniz {selected.duration_days} gün
              boyunca aktive olacaktır.
            </p>

            <div className="gc-label mb-2">BTC ADRESİ</div>
            <div className="flex items-center gap-2 border border-[#222] bg-[#050505] p-3 font-mono text-xs mb-4 break-all rounded-lg">
              <span data-testid="btc-address">
                {settings.btc_address || "Adres henüz ayarlanmadı"}
              </span>
              <button
                type="button"
                data-testid="btc-address-copy"
                onClick={() =>
                  navigator.clipboard.writeText(settings.btc_address || "")
                }
                className="text-[#A1A1AA] hover:text-[#9D4EDD]"
              >
                <Copy size={12} />
              </button>
            </div>

            <label className="gc-label block mb-2">İŞLEM ID (TXID)</label>
            <input
              data-testid="tx-input"
              className="gc-input mb-4"
              placeholder="TXID..."
              value={tx}
              onChange={(e) => setTx(e.target.value)}
              required
            />

            {err && (
              <div className="text-[#9D4EDD] text-sm font-mono border border-[#9D4EDD]/40 bg-[#9D4EDD]/5 p-3 mb-4 rounded-lg">
                {err}
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="gc-btn-ghost"
                onClick={() => setSelected(null)}
                data-testid="buy-cancel"
              >
                VAZGEÇ
              </button>
              <button data-testid="buy-submit" className="gc-btn-primary" disabled={busy}>
                {busy ? "GÖNDERİLİYOR…" : "SİPARİŞİ GÖNDER"}
              </button>
            </div>
          </form>
        </div>
      )}

      {ok && (
        <div
          className="text-[#00FF66] text-sm font-mono border border-[#00FF66]/40 bg-[#00FF66]/5 p-3 rounded-lg"
          data-testid="shop-success"
        >
          {ok}
        </div>
      )}

      {/* My Orders */}
      <div className="gc-card p-5">
        <div className="gc-label mb-1">SİPARİŞLERİM</div>
        <h3 className="font-heading text-lg font-bold mb-4">Geçmiş & Bekleyen</h3>
        {orders.length === 0 && (
          <div className="text-sm text-[#A1A1AA]">Henüz sipariş yok.</div>
        )}
        <div className="space-y-2.5">
          {orders.map((o) => (
            <div
              key={o.id}
              className="flex items-center justify-between border-b border-[#141414] pb-2.5 text-sm"
              data-testid="order-row"
            >
              <div className="min-w-0">
                <div className="font-heading font-bold text-white">
                  {o.tier_name} — ₿{o.price_btc}
                </div>
                <div className="text-xs font-mono text-[#A1A1AA] break-all">
                  TX: {o.tx_id}
                </div>
                <div className="text-[10px] text-[#525252] font-mono">
                  {o.created_at?.slice(0, 16).replace("T", " ")}
                </div>
              </div>
              <span
                className={`gc-badge ${
                  o.status === "approved"
                    ? "gc-badge-green"
                    : o.status === "rejected"
                    ? "gc-badge-red"
                    : "gc-badge-gold"
                }`}
              >
                {o.status?.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
